import { useState, useEffect } from 'react'
import { Plus } from 'lucide-react'
import { v4 as uuid } from 'uuid'
import AddItemModal from './AddItemModal'
import CarouselItem from './CarouselItem'
import { HeroItem } from 'interfaces/widget-types'
import { useNotificationBar } from 'context/notificationContext'
import { useUploadFiles } from 'hooks/upload'
import { Button } from 'components/ui/button'
import { ToggleField } from 'components/styledFormElements/ToggleField'
import { getImageSrc } from 'services/util'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { useGetItemsParent } from 'hooks/category-manage'
import {
    collectRedirectionParentRequests,
    createParentCache,
    enrichRedirectionItemsWithParents,
    resolveParentsForRequests,
} from '../VideoCarousel/linkedParentResolver'

interface Props {
    widget: {
        label: string
        code: string
        value: number | string
    }
    initialData?: any
    items?: HeroItem[]
    onSave: (payload: any) => void
    onCancel?: () => void;
}

const MAX_ITEMS = 6

const parseHeroItemsForEdit = (items: any[]): HeroItem[] => {
    if (!items?.length) return [];
    return items.map((item: any) => ({
        id: item.id || uuid(),
        name: item.name || item.title || '',
        thumbnail: item.thumbnail || '',
        redirection_to: item.redirection_to,
        redirection_id: String(item.redirection_id || ''),
        redirection_type: item.redirection_type || '',
        is_webview: Boolean(item.is_webview),
        webview_url: item.webview_url || '',
        video_id: item.video_id || '',
        imageFile: null,
    }));
};

/* ---------- Component ---------- */

const HomePageBanner = ({ widget, initialData, items = [], onSave }: Props) => {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [heroItems, setHeroItems] = useState<HeroItem[]>(() =>
        items?.length > 0 ? JSON.parse(JSON.stringify(items)) : []
    )
    const [enableRotation, setEnableRotation] = useState(
        Boolean(initialData?.data?.enable_rotation)
    );

    const [widgetName, setWidgetName] = useState(
        initialData?.name || 'Home Page Banner'
    );
    const notification = useNotificationBar()
    const uploadMutation = useUploadFiles();
    const itemsParentMutation = useGetItemsParent();

    /* ---------- Handlers ---------- */

    const addItem = (data: Omit<HeroItem, 'id'>) => {
        const newItem: HeroItem = {
            id: uuid(),
            name: data.name || '',
            thumbnail: data.thumbnail || '',
            redirection_to: data.redirection_to,
            redirection_id: data.redirection_id,
            redirection_type: data.redirection_type || '',
            is_webview: Boolean(data.is_webview),
            webview_url: data.webview_url || '',
            imageFile: data.imageFile || null,
            video_id: data.video_id || '',
        };

        setHeroItems((prev) => [...prev, newItem])
        setIsModalOpen(false)
    }

    const moveItem = (from: number, to: number) => {
        setHeroItems((prev) => {
            if (to < 0 || to >= prev.length) return prev

            const updated = [...prev]
                ;[updated[from], updated[to]] = [updated[to], updated[from]]
            return updated
        })
    }

    const deleteItem = (index: number) => {
        setHeroItems((prev) => prev.filter((_, i) => i !== index))
    }

    useEffect(() => {
        if (!initialData) return;

        if (initialData.name) {
            setWidgetName(initialData.name);
        }
        const rawItems = initialData?.data?.widget_items;
        setHeroItems(
            rawItems?.length > 0
                ? parseHeroItemsForEdit(JSON.parse(JSON.stringify(rawItems)))
                : []
        );
        setEnableRotation(Boolean(initialData?.data?.enable_rotation));
    }, [initialData]);

    const handleSave = async () => {
        if (heroItems.length === 0) {
            notification.push({
                title: 'Error',
                message: 'Please add at least one item',
                type: 'error',
            })
            return
        }

        try {
            const cache = createParentCache();
            const parentRequests = collectRedirectionParentRequests(heroItems);

            if (parentRequests.length > 0) {
                await resolveParentsForRequests(
                    itemsParentMutation.mutateAsync,
                    cache,
                    parentRequests
                );
            }

            const skeletonItems = heroItems.map((item) => {
                const isWebview = Boolean(item.is_webview) || item.redirection_type === 'WEB_VIEW';
                const webviewUrl = item.webview_url || item.redirection_id || '';

                return {
                    name: item.name,
                    redirection_id: item.redirection_id,
                    redirection_to: isWebview ? webviewUrl : String(item.redirection_to).toUpperCase(),
                    redirection_type: isWebview ? 'WEB_VIEW' : String(item.redirection_to).toUpperCase(),
                    is_webview: isWebview,
                    webview_url: isWebview ? webviewUrl : '',
                    video_id: item.video_id || '',
                    thumbnail: item.thumbnail || '',
                };
            });

            const enrichedItems = enrichRedirectionItemsWithParents(skeletonItems, cache);

            const widgetItem = await Promise.all(
                enrichedItems.map(async (item, index) => {
                    const source = heroItems[index];
                    let thumbnail = String(item.thumbnail || '');

                    if (source.imageFile) {
                        const formData = new FormData()
                        formData.append('file', source.imageFile)
                        formData.append('customPath', 'widgets/items/images')

                        const res = await uploadMutation.mutateAsync({
                            data: formData,
                        })
                        thumbnail = res?.data?.data?.key || thumbnail;
                    }

                    return {
                        name: item.name,
                        redirection_id: item.redirection_id,
                        redirection_to: item.redirection_to,
                        redirection_type: item.redirection_type || '',
                        is_webview: Boolean(item.is_webview),
                        webview_url: item.webview_url || '',
                        thumbnail,
                        video_id: item.video_id || '',
                        show_id: item.show_id || '',
                        show_name: item.show_name || '',
                        collection_id: item.collection_id || '',
                        collection_name: item.collection_name || '',
                        // Adding for the category id and name
                        // category_id: item.category_id || '',
                        // category_name: item.category_name || '',
                        category_id: item.redirection_to === 'CATEGORY' ? item.redirection_id : item.category_id || '',
                        category_name: item.redirection_to === 'CATEGORY' ? item.name : item.category_name || '',
                    };
                })
            );

            const finalData = {
                name: widgetName || widget.label,
                description: '',
                widget_code: widget?.code,
                widget_type: Number(widget.value),
                data: {
                    widget_code: widget?.code,
                    source_type: 'MANUAL',
                    content_type: 'STATIC',
                    widget_items: widgetItem,
                    show_id: '',
                    show_name: '',
                    collection_id: '',
                    collection_name: '',
                    category_id: '',
                    category_name: '',
                    enable_rotation: enableRotation,
                },
            }
            onSave(JSON.parse(JSON.stringify(finalData)))

            notification.push({
                title: 'Success',
                message: 'Homepage Banner Carousel widget saved successfully',
                type: 'success',
            })
        } catch {
            notification.push({
                title: 'Error',
                message: 'Failed to resolve parent data or upload files. Please try again.',
                type: 'error',
            })
        }
    }

    const handleCancel = () => {
        onSave(initialData || null);
    };

    /* ---------- Render ---------- */

    return (
        <div className="space-y-6 mb-1">
            {/* Header */}
            <div className="p-2 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl" />
            </div>

            {/* Add Button + rotation toggle */}
            <div className="flex items-center justify-between gap-4 !mt-0">
                <div className="flex items-center gap-2">
                    <Button
                        disabled={heroItems?.length >= MAX_ITEMS}
                        onClick={() => setIsModalOpen(true)}
                        className="btn-primary flex items-center gap-2 h-10"
                        color="blue-accent"
                    >
                        <Plus size={16} />
                        Add Item
                    </Button>

                    <div className="relative group">
                        <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                            Click to upload a banner image and set where it redirects (episode/show/collection/category). Max 6 items with circular carousel navigation.
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3 h-10">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Enable Rotation
                    </span>
                    <ToggleField
                        checked={enableRotation}
                        onChange={(e) => setEnableRotation(e.target.checked)}
                    />
                </div>
            </div>

            <div className="flex items-center justify-between">
                <label className="block text-sm font-medium mb-2 dark:text-white">Selected Items</label>
                <div className="relative flex-item group mb-2 ml-2 mt-0">
                    <div className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 mb-2">
                        <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">
                            {heroItems?.length ?? 0}/{MAX_ITEMS}
                        </span>
                    </div>
                    <div className="absolute bottom-full right-0 -translate-y-0 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                        You can select up to {MAX_ITEMS} items <span className='font-medium'>({heroItems?.length} selected)</span>.
                    </div>
                </div>
            </div>

            <div className="bg-white border border-gray-300 rounded-lg p-2 min-h-32 !mt-0 grid grid-cols-1 gap-2 dark:bg-slate-700 dark:border-slate-600">
                {(heroItems?.length ?? 0) === 0 ? (
                    <p className="text-sm text-gray-500">No banners added. Click ‘Add Item’ to upload images and set redirection.</p>
                ) : (
                    heroItems.map((item, index) => (
                        <CarouselItem
                            key={item.id}
                            item={item}
                            index={index}
                            total={heroItems.length}
                            onMoveUp={() => moveItem(index, index - 1)}
                            onMoveDown={() => moveItem(index, index + 1)}
                            onDelete={() => deleteItem(index)}
                        >
                            <div key={item.id} className="flex items-center gap-3">
                                <img
                                    src={getImageSrc(item.thumbnail)}
                                    alt="Thumbnail"
                                    className="w-20 h-12 object-cover rounded border"
                                />
                                <div>
                                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                        {item.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        ID: {item.redirection_id}
                                    </p>
                                    <span className="text-gray-400">
                                        ({item.redirection_to})
                                    </span>
                                </div>
                            </div>
                        </CarouselItem>
                    ))
                )}
            </div>

            <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-200 dark:border-slate-600">
                <button
                    type="button"
                    onClick={handleCancel}
                    className="px-6 py-2 bg-gray-300 dark:bg-slate-600 text-gray-800 dark:text-white rounded hover:bg-gray-400 dark:hover:bg-slate-500"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Save
                </button>
            </div>

            <AddItemModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onAdd={addItem}
                selectedWidget={widget.label}
                redirectOptions={[
                    { value: 'Episode', label: 'Episode' },
                    { value: 'Show', label: 'Show' },
                    { value: 'Collection', label: 'Collection' },
                    { value: 'Category', label: 'Category' },
                    { value: 'Web View', label: 'Web View' },
                    { value: 'Other', label: 'Other' },
                ]}
            />
        </div>
    )
}

export default HomePageBanner
